import { useAIEdit } from 'hooks/useAIEdit'
import { FC, useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiButton, EuiButtonIcon, EuiCallOut, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiSpacer, EuiTitle, EuiToolTip } from '@elastic/eui'

import { AIEditFlyoutState } from 'api/interfaces/AIEditTypes'
import { AIPlan } from 'api/interfaces/AIPlan'
import { Persona } from 'api/interfaces/Persona'
import { useAiPlanRemovePersonaMutation, useGetAIPlanPersonasSectionQuery, useUpdateAIPlanPersonasIntroductionSectionMutation, useHideAIPlanPersonasSectionMutation, useAddAIPlanPersonasCustomSectionMutation, useUpdateAIPlanPersonasCustomSectionMutation, useDeleteAIPlanPersonasCustomSectionMutation, useUpdateAIPlanPersonasPageTitleMutation, useAiEditAIPlanMutation } from 'api/rtkQueryApi/platform/aiPlansApi'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import AIEditPromptFlyout from 'features/planner/components/AIEditPromptFlyout'
import PersonaList from 'features/planner/components/AIPlan/Personas/PersonaList'
import { AddCustomSectionButton } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/AddCustomSectionButton'
import CustomizableSection from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/CustomizableSection'
import EditCustomSectionFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditCustomSectionFlyout'
import EditPageTitleFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditPageTitleFlyout'
import { RenderCustomSections } from 'features/planner/pages/MasterCampaignPlanPage/components/shared/RenderCustomSections'
import { downloadPdf } from 'utils/pdfDownloader'

import { selectAIPlanEditMode } from '../../aiPlanSlice'
import AIPlanPersonasPdf from '../AIPlanPersonasPdf'
import EditAIPlanPersonaFlyout, { EditAIPlanPersonaFlyoutMode } from './EditAIPlanPersonaFlyout'
import ReorderAIPlanPersonasFlyout from './ReorderAIPlanPersonasFlyout'

export interface AIPlanPersonasProps {
  aiPlan: AIPlan
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
}

interface EditPersonaFlyoutState {
  mode: EditAIPlanPersonaFlyoutMode
  selectedPersona?: Persona
}

type SectionType = 'introduction'
type PersonaCustomSectionPosition = 'abovePersonaList'

const DEFAULT_INTRODUCTION_CONTENT = 'The following personas are descriptive of the types of people we will be targeting. While fictional generalizations, they are based on research and market analysis. They will be used both in the targeting of your ad campaigns and in the crafting of your ads.'

const AIPlanPersonas: FC<AIPlanPersonasProps> = ({ aiPlan, onAllStaffPdfsClick, staffPdfDisabledReason }) => {
  const editMode = useSelector(selectAIPlanEditMode)
  const { currentAccount } = useSelector((state: RootState) => state.app)

  const [editPersonaFlyoutOpen, setEditPersonaFlyoutOpen] = useState<EditPersonaFlyoutState | null>(null)
  const [isReorderFlyoutOpen, setIsReorderFlyoutOpen] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [aiEditFlyoutState, setAiEditFlyoutState] = useState<AIEditFlyoutState | null>(null)

  const [editingSectionType, setEditingSectionType] = useState<SectionType | null>(null)
  const [deletingSectionType, setDeletingSectionType] = useState<SectionType | null>(null)
  const [addingCustomSectionPosition, setAddingCustomSectionPosition] = useState<PersonaCustomSectionPosition | null>(null)
  const [editingCustomSection, setEditingCustomSection] = useState<{ position: PersonaCustomSectionPosition; index: number } | null>(null)
  const [deletingCustomSection, setDeletingCustomSection] = useState<{ position: PersonaCustomSectionPosition; index: number } | null>(null)
  const [editingPageTitle, setEditingPageTitle] = useState(false)

  const { data: personasSection } = useGetAIPlanPersonasSectionQuery({ aiPlanId: aiPlan.id })

  const [removePersonaMutation, { isLoading: isRemovingPersona }] = useAiPlanRemovePersonaMutation()
  const [updateIntroduction, { isLoading: isUpdatingIntroduction }] = useUpdateAIPlanPersonasIntroductionSectionMutation()
  const [hideSection, { isLoading: isHidingSection }] = useHideAIPlanPersonasSectionMutation()
  const [addCustomSection, { isLoading: isAddingCustomSection }] = useAddAIPlanPersonasCustomSectionMutation()
  const [updateCustomSection, { isLoading: isUpdatingCustomSection }] = useUpdateAIPlanPersonasCustomSectionMutation()
  const [deleteCustomSection, { isLoading: isDeletingCustomSection }] = useDeleteAIPlanPersonasCustomSectionMutation()
  const [updatePageTitle, { isLoading: isUpdatingPageTitle }] = useUpdateAIPlanPersonasPageTitleMutation()
  const [aiEditAIPlan] = useAiEditAIPlanMutation()

  const { submit: submitAIEdit, isSubmitting: isAIEditSubmitting } = useAIEdit({
    mutationFn: aiEditAIPlan,
    entityId: aiPlan.id,
    onSuccess: () => setAiEditFlyoutState(null)
  })

  const showActions = editMode

  const onEditPersonaClick = (persona: Persona) => {
    setEditPersonaFlyoutOpen({ mode: EditAIPlanPersonaFlyoutMode.Edit, selectedPersona: persona })
  }

  const onAIAddPersonaClick = () => {
    setAiEditFlyoutState({ targetType: 'AddPersona', targetId: aiPlan.id, targetLabel: 'Add Persona', promptLabel: 'Describe the new persona you want AdCritter AI to create', promptPlaceholder: 'e.g., A 35-45 year old suburban homeowner who values quality over price, researches extensively online before purchasing, and is motivated by family security and long-term investment' })
  }

  const onDeletePersonaClick = async (personaToDelete: Persona): Promise<void> => {
    if (!aiPlan?.id || !currentAccount) return
    await removePersonaMutation({
      organizationId: aiPlan.organizationId,
      accountId: currentAccount.id,
      aiPlanId: aiPlan.id,
      personaName: personaToDelete.name,
      personaRank: personaToDelete.rank
    })
  }

  const handleSaveSection = async (title: string, content: string, showTitle: boolean) => {
    if (!editingSectionType) return

    await updateIntroduction({
      aiPlanId: aiPlan.id,
      title,
      content,
      showTitle
    })

    setEditingSectionType(null)
  }

  const confirmDeleteSection = async () => {
    if (!deletingSectionType) return

    await hideSection({
      aiPlanId: aiPlan.id,
      sectionType: deletingSectionType
    })

    setDeletingSectionType(null)
  }

  const handleSaveCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingCustomSectionPosition) {
      const existingSections = personasSection?.[addingCustomSectionPosition] || []
      await addCustomSection({
        aiPlanId: aiPlan.id,
        position: addingCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingCustomSectionPosition(null)
    } else if (editingCustomSection) {
      const existingSections = personasSection?.[editingCustomSection.position] || []
      await updateCustomSection({
        aiPlanId: aiPlan.id,
        position: editingCustomSection.position,
        existingSections,
        sectionIndex: editingCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingCustomSection(null)
    }
  }

  const confirmDeleteCustomSection = async () => {
    if (!deletingCustomSection) return

    const existingSections = personasSection?.[deletingCustomSection.position] || []
    await deleteCustomSection({
      aiPlanId: aiPlan.id,
      position: deletingCustomSection.position,
      existingSections,
      sectionIndex: deletingCustomSection.index
    })

    setDeletingCustomSection(null)
  }

  const handleSavePageTitle = async (newTitle: string) => {
    await updatePageTitle({
      aiPlanId: aiPlan.id,
      pageTitle: newTitle
    })
    setEditingPageTitle(false)
  }

  const getDeleteCustomSectionTitle = (): string => {
    if (!deletingCustomSection) return 'Delete Custom Section'

    const sections = personasSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Delete ${title}`
    }

    return 'Delete Custom Section'
  }

  const getDeleteCustomSectionMessage = (): string => {
    if (!deletingCustomSection) return 'Are you sure you want to delete this custom section?'

    const sections = personasSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Are you sure you want to delete ${title}?`
    }

    return 'Are you sure you want to delete this custom section?'
  }

  const isUpdatingAnySection = isUpdatingIntroduction || isAddingCustomSection || isUpdatingCustomSection

  const handlePdfDownload = async () => {
    try {
      setIsGeneratingPdf(true)
      const filename = `${aiPlan.name} - Personas.pdf`
      await downloadPdf(<AIPlanPersonasPdf aiPlan={aiPlan} personaSection={personasSection} />, filename)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <>
      <EuiFlexGroup justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiTitle size='m'>
                <h3>{personasSection?.pageTitle || 'Persona Prioritization'}</h3>
              </EuiTitle>
            </EuiFlexItem>
            {editMode && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='Edit Title'>
                    <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={() => setEditingPageTitle(true)} aria-label='Edit Title Persona Prioritization' size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup>
            {onAllStaffPdfsClick && (
              <EuiFlexItem grow={false}>
                {staffPdfDisabledReason ? (
                  <EuiToolTip content={staffPdfDisabledReason}>
                    <EuiButton size='s' color='primary' disabled>
                      All Staff PDFs
                    </EuiButton>
                  </EuiToolTip>
                ) : (
                  <EuiButton size='s' color='primary' onClick={onAllStaffPdfsClick}>
                    All Staff PDFs
                  </EuiButton>
                )}
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton size='s' color='primary' onClick={handlePdfDownload} isLoading={isGeneratingPdf} disabled={isGeneratingPdf}>
                Client PDF
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiHorizontalRule />

      {aiPlan.personaMode === 'Ignore' ? (
        <EuiCallOut title='Personas were not used for this plan' iconType='iInCircle' />
      ) : (
        <>
          {!personasSection?.introduction?.hideAll && (
            <>
              <CustomizableSection section={personasSection?.introduction} defaultTitle='' defaultContent={DEFAULT_INTRODUCTION_CONTENT} defaultShowTitle={false} separator='spacer' onEdit={() => setEditingSectionType('introduction')} onDelete={() => setDeletingSectionType('introduction')} showActions={editMode} />
              <EuiSpacer size='xl' />
            </>
          )}

          <RenderCustomSections sections={personasSection?.abovePersonaList} position={'abovePersonaList' as PersonaCustomSectionPosition} editMode={editMode} onEdit={(position: PersonaCustomSectionPosition, index: number) => setEditingCustomSection({ position, index })} onDelete={(position: PersonaCustomSectionPosition, index: number) => setDeletingCustomSection({ position, index })} />
          {editMode && (
            <>
              <AddCustomSectionButton position={'abovePersonaList' as PersonaCustomSectionPosition} onClick={() => setAddingCustomSectionPosition('abovePersonaList')} />
              <EuiSpacer size='xl' />
            </>
          )}

          <PersonaList
            personas={aiPlan.personas || []}
            onEditPersonaClick={showActions ? onEditPersonaClick : undefined}
            onAIEditPersonaClick={showActions ? (persona: Persona) => setAiEditFlyoutState({ targetType: 'Persona', targetId: persona.id, targetLabel: persona.name }) : undefined}
            onAIAddPersonaClick={showActions ? onAIAddPersonaClick : undefined}
            onReorderPersonasClick={showActions ? () => setIsReorderFlyoutOpen(true) : undefined}
            onDeletePersonaClick={showActions ? onDeletePersonaClick : undefined}
            onDeletePersonaLoading={isRemovingPersona}
            showActions={showActions}
          />
        </>
      )}

      {editPersonaFlyoutOpen && <EditAIPlanPersonaFlyout editMode={editPersonaFlyoutOpen.mode} selectedPersona={editPersonaFlyoutOpen.selectedPersona} onClose={() => setEditPersonaFlyoutOpen(null)} />}

      {isReorderFlyoutOpen && <ReorderAIPlanPersonasFlyout onClose={() => setIsReorderFlyoutOpen(false)} />}

      {aiEditFlyoutState && <AIEditPromptFlyout targetType={aiEditFlyoutState.targetType} targetLabel={aiEditFlyoutState.targetLabel} promptLabel={aiEditFlyoutState.promptLabel} promptPlaceholder={aiEditFlyoutState.promptPlaceholder} onSubmit={(_prompt: string, _constraints?: unknown, deepThink?: boolean) => submitAIEdit(_prompt, aiEditFlyoutState, deepThink)} onClose={() => setAiEditFlyoutState(null)} isSubmitting={isAIEditSubmitting} showBlueprintWarning />}

      {editingSectionType && <EditCustomSectionFlyout section={personasSection?.introduction} defaultTitle='' defaultContent={DEFAULT_INTRODUCTION_CONTENT} defaultShowTitle={false} flyoutTitle='Edit Introduction' onSave={handleSaveSection} onClose={() => setEditingSectionType(null)} isLoading={isUpdatingAnySection} showBlueprintWarning />}

      {(addingCustomSectionPosition || editingCustomSection) && (
        <EditCustomSectionFlyout
          section={editingCustomSection ? personasSection?.[editingCustomSection.position]?.[editingCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent=''
          defaultShowTitle={true}
          flyoutTitle={addingCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveCustomSection}
          onClose={() => {
            setAddingCustomSectionPosition(null)
            setEditingCustomSection(null)
          }}
          isLoading={isUpdatingAnySection}
          showBlueprintWarning
        />
      )}

      {deletingSectionType && <AcConfirmModal message='Are you sure you want to delete this section?' title='Delete Introduction' onCancel={() => setDeletingSectionType(null)} onConfirm={confirmDeleteSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isHidingSection} confirmButtonDisabled={isHidingSection} />}

      {deletingCustomSection && <AcConfirmModal message={getDeleteCustomSectionMessage()} title={getDeleteCustomSectionTitle()} onCancel={() => setDeletingCustomSection(null)} onConfirm={confirmDeleteCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingCustomSection} confirmButtonDisabled={isDeletingCustomSection} />}

      {editingPageTitle && <EditPageTitleFlyout currentTitle={personasSection?.pageTitle || 'Persona Prioritization'} defaultTitle='Persona Prioritization' onSave={handleSavePageTitle} onClose={() => setEditingPageTitle(false)} isLoading={isUpdatingPageTitle} showBlueprintWarning />}
    </>
  )
}

export default AIPlanPersonas
